/**
 * Puzzle 3 — Timed Resonance.
 *
 * Spinning gears block a rusted blockage. Basil + Minna must:
 *   1. Activate a Chronos Bubble to slow the gears.
 *   2. Land a Resonant Drop inside the bubble's window.
 * The drop shatters the blockage only while the gears are slowed.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";

import { GameEvents } from "../../../config/events";
import { CLOCKWORK_CANOPY, COLORS } from "../../../config/GameConfig";
import { tagAs } from "../../PlaceholderTag";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

const CUE = {
  slow: { cat: "both", parts: [{ action: "chronosBubble" }, { text: "together: slow the gears" }] },
  drop: { cat: "both", parts: [{ action: "resonantDrop" }, { text: "now shatter it" }] },
} satisfies Record<string, CueDef>;
const OBJECTIVE = "Slip through the timed gate";
const GEARS_ANCHOR = { x: 61.5, y: 7.4, z: 0 };
const BLOCKAGE_ANCHOR = { x: 67, y: 6.6, z: 0 };

export class TimedResonancePuzzle implements PuzzleModule {
  readonly id = "timedResonance";
  readonly displayName = "Timed Resonance";

  private ctx!: PuzzleContext;

  private timedBlockage: Mesh | null = null;
  private rustMat: PBRMaterial | null = null;
  private spinGear1!: Mesh;
  private spinGear2!: Mesh;
  private gearSpinMat!: PBRMaterial;

  private gearSpinSpeed = 2.0;
  private chronosSlowActive = false;
  private chronosBubbleEverUsed = false;
  private shattered = false;

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene, shadowGenerator } = ctx;

    // ── Rusted blockage (the target) ──
    this.timedBlockage = MeshBuilder.CreateBox(
      "timedBlockage",
      { width: 3, height: 2.5, depth: 2 },
      scene,
    );
    this.timedBlockage.position = new Vector3(67, 5, 0);
    this.rustMat = new PBRMaterial("timedRustMat", scene);
    this.rustMat.albedoColor = new Color3(0.55, 0.25, 0.1);
    this.rustMat.metallic = 0.7;
    this.rustMat.roughness = 0.6;
    this.timedBlockage.material = this.rustMat;
    shadowGenerator.addShadowCaster(this.timedBlockage);
    tagAs(this.timedBlockage, "placeholder");

    // ── Spinning gears (visual indicators) ──
    this.gearSpinMat = new PBRMaterial("gearSpinMat", scene);
    this.gearSpinMat.albedoColor = Color3.FromHexString(
      CLOCKWORK_CANOPY.COLORS.BRASS_WARM,
    );
    this.gearSpinMat.metallic = 0.9;
    this.gearSpinMat.roughness = 0.2;

    this.spinGear1 = MeshBuilder.CreateTorus(
      "spinGear1",
      { diameter: 3, thickness: 0.3, tessellation: 24 },
      scene,
    );
    this.spinGear1.position = new Vector3(60, 6, 0);
    this.spinGear1.material = this.gearSpinMat;
    tagAs(this.spinGear1, "placeholder");

    this.spinGear2 = MeshBuilder.CreateTorus(
      "spinGear2",
      { diameter: 2.5, thickness: 0.25, tessellation: 24 },
      scene,
    );
    this.spinGear2.position = new Vector3(63, 5, 0);
    this.spinGear2.material = this.gearSpinMat;
    tagAs(this.spinGear2, "placeholder");

    ctx.registerInteractive({
      mesh: this.timedBlockage,
      hidden: false,
      state: "revealed",
    });

    // ── Chronos Bubble slows gears ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CHRONOS_BUBBLE_TRIGGERED, () => {
        this.chronosSlowActive = true;
        this.chronosBubbleEverUsed = true;
        this.gearSpinSpeed = 0.3;
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CHRONOS_BUBBLE_ENDED, () => {
        this.chronosSlowActive = false;
        if (!this.shattered) {
          this.gearSpinSpeed = 2.0;
        }
      }),
    );

    // ── Resonant Drop inside the bubble window shatters the blockage ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.RESONANT_DROP_TRIGGERED, (data) => {
        if (this.shattered || !this.timedBlockage) return;

        const blockPos = this.timedBlockage.position;
        const dropPos = new Vector3(data.x, data.y, data.z);
        const dist = Vector3.Distance(blockPos, dropPos);
        if (dist <= data.radius && this.chronosSlowActive) {
          this.shattered = true;
          this.timedBlockage.dispose();
          this.timedBlockage = null;
          this.rustMat?.dispose();
          this.rustMat = null;
          this.gearSpinSpeed = 0;

          ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, {
            puzzleId: this.id,
          });
          ctx.gameEvents.emit(GameEvents.BLOCKAGE_SHATTERED, {
            key: "timedBlockage",
          });
          // Forensic-QA event: mirrors FinalBlockage's emission so the
          // recorder sees every Resonant-Drop-triggered shatter the same
          // way, regardless of level.
          ctx.gameEvents.emit(GameEvents.SHOCKWAVE_SHATTERED, {
            x: data.x,
            y: data.y,
            z: data.z,
            radius: data.radius,
            blockageKey: "timedBlockage",
          });
          ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: "Blockage shattered! Path cleared.",
            color: COLORS.COPPER,
          });
        }
      }),
    );
  }

  update(dt: number, _elapsed: number): void {
    // Spinning gears animation
    if (this.gearSpinSpeed > 0) {
      this.spinGear1.rotation.z += this.gearSpinSpeed * dt;
      this.spinGear2.rotation.z -= this.gearSpinSpeed * 1.3 * dt;
    }
  }

  getCue(): PuzzleCue | null {
    if (this.shattered) return null;
    return this.chronosSlowActive
      ? { objective: OBJECTIVE, cue: CUE.drop, anchor: BLOCKAGE_ANCHOR }
      : { objective: OBJECTIVE, cue: CUE.slow, anchor: GEARS_ANCHOR };
  }

  getState(): PuzzleState {
    if (this.shattered) return { solved: true, progress: 1 };
    // Soft progress indicator: once the player has tried Chronos Bubble at
    // least once, show 50% — they understand half the puzzle.
    return {
      solved: false,
      progress: this.chronosBubbleEverUsed ? 0.5 : 0,
    };
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "timedBlockage", position: { x: 67, y: 5 }, radius: 1.8, allowedCat: "either", label: "Timed blockage (Chronos + Resonant Drop)" },
        { id: "spinGear_0",    position: { x: 60, y: 6 }, radius: 1.2, allowedCat: "basil", label: "Spinning gear 0" },
        { id: "spinGear_1",    position: { x: 63, y: 5 }, radius: 1.2, allowedCat: "basil", label: "Spinning gear 1" },
        // drop_stage is a staging position the QA navigator can reach.
        // Previously y=5 — which is in midair (no platform there). The
        // navigator would oscillate trying to reach a floating point.
        // plat_s3_2 is the only platform in the area (top y=3.2); put
        // drop_stage ON it so `navigate to drop_stage` actually lands
        // somewhere stable while the cat is still within
        // RESONANT_DROP_RANGE=5u of the blockage at (67, 5).
        { id: "drop_stage",    position: { x: 66, y: 4 }, radius: 1.5, allowedCat: "either", label: "Drop staging (on plat_s3_2, within 5u of blockage)" },
      ],
      traversals: [
        // Co-op chord: Chronos Bubble (g+n) slows the gears, then
        // Resonant Drop (f+m) shatters the blockage. Both fire from
        // the spawn area (PUZZLE_ORIGINS at 65, 1) — already inside
        // RESONANT_DROP_RANGE (7.5u) and CHRONOS_BUBBLE_RANGE (7.5u).
        // No traversal needed; the recipe documents the canonical
        // chord sequence so a future plan can dispatch via traverse.
        {
          id: "coop_chronos_then_drop",
          kind: "coop",
          cat: "either",
          from: "drop_stage",
          to: "timedBlockage",
          successWindowMs: 6_000,
          recipe: [
            // g+n composite — Chronos Bubble.
            { keyDown: "g" },
            { keyDown: "n" },
            { waitMs: 200 },
            { keyUp: "n" },
            { keyUp: "g" },
            // Bubble takes ~500ms to engage; wait for chronosSlowActive.
            { waitMs: 500 },
            // f+m composite — Resonant Drop inside the bubble window.
            { keyDown: "f" },
            { keyDown: "m" },
            { waitMs: 200 },
            { keyUp: "m" },
            { keyUp: "f" },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   { solved?: boolean }
   *
   * TimedResonance is a one-shot "Chronos Bubble + Resonant Drop" combo.
   * There are no intermediate mutable flags beyond the "shattered" bit
   * (and `chronosBubbleEverUsed`, which only affects the progress number).
   * Only `{ solved: true }` is meaningful — it disposes the blockage and
   * emits the same BLOCKAGE_SHATTERED / SHOCKWAVE_SHATTERED / PUZZLE_SOLVED
   * trio the real co-op path fires.
   */
  seed(state: Record<string, unknown>): void {
    if (state.solved === true && !this.shattered && this.timedBlockage) {
      const blockPos = this.timedBlockage.position.clone();
      this.shattered = true;
      this.timedBlockage.dispose();
      this.timedBlockage = null;
      this.rustMat?.dispose();
      this.rustMat = null;
      this.gearSpinSpeed = 0;
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
      this.ctx.gameEvents.emit(GameEvents.BLOCKAGE_SHATTERED, {
        key: "timedBlockage",
      });
      this.ctx.gameEvents.emit(GameEvents.SHOCKWAVE_SHATTERED, {
        x: blockPos.x,
        y: blockPos.y,
        z: blockPos.z,
        radius: 2,
        blockageKey: "timedBlockage",
      });
    }
  }

  dispose(): void {
    this.timedBlockage?.dispose();
    this.rustMat?.dispose();
    this.spinGear1.dispose();
    this.spinGear2.dispose();
    this.gearSpinMat.dispose();
  }
}
